/**
 * An organisation's own brand — the END-CUSTOMER half of CRATUS 2.0 #2 (Lumen-BE migration 0083,
 * `modules/organisations/brand.ts`).
 *
 * The product's brand is compiled in (`src/product/`): a Cratus console says Cratus. This is the
 * customer's: an operator beneath Cratus sets its logo and the name its people know it by, and the
 * console chrome shows them beside the Cratus mark. A sub-organisation with none shows the nearest
 * ancestor's, which the API resolves — the console never walks the tree upward.
 *
 * THE LOGO NEVER TRAVELS THROUGH THE API, and never into an `<img src>` from Storage: the API signs
 * an upload for one key, the browser PUTs the file straight to Storage, and the API confirms what
 * arrived (`uploadBrandLogo`). To show it, the signed path is fetched into a `blob:` URL, because
 * the console's CSP admits images from itself, `data:` and `blob:` only.
 *
 * KEPT FOR FIRST PAINT. A header that pops its customer logo in a beat after every reload is the
 * jerk the loader work removed, so a small logo is remembered as a `data:` URL in this browser
 * (`brand-cache.ts`) and painted at once; the query confirms or replaces it.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { request } from './client';
import { fetchStoredImage, StorageUploadError } from './cabinets';
import { SUPABASE_URL } from '@/lib/supabase';
import { tenancyKeys } from './tenancy';
import { readKeptBrand, keepBrand, type KeptBrand } from './brand-cache';

export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export type LogoType = (typeof LOGO_TYPES)[number];
/** The API's and the bucket's ceiling (Lumen-BE `MAX_LOGO_BYTES`). */
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;
export const MAX_BRAND_NAME = 60;

export interface BrandView {
  name: string | null;
  /** A signed path under the storage root, valid for minutes — fetch it, never keep it. */
  logo: { path: string; contentType: LogoType } | null;
  /** When the name or logo last changed: what a kept copy of the logo is matched on. */
  updatedAt: string | null;
}

export interface Brand {
  /** This organisation's own brand: what the Organisation screen edits. */
  own: BrandView;
  /**
   * What its console shows: its own, else the nearest ancestor's. `from` names that ancestor only
   * when the caller already reaches it.
   */
  effective: BrandView & { inherited: boolean; from: { id: string; name: string } | null };
  /** Palette and accent's rule: `tenants.edit`, and the organisation is yours or beneath you. */
  editable: boolean;
}

export const brandKey = (orgId: string) => [...tenancyKeys.orgs, orgId, 'brand'] as const;

/**
 * Every request here is answered IN the organisation it is about (`organisation`, as
 * `api.within`), so a query still keyed to the previous selection never refetches under the next
 * one and 404s.
 */
const inOrg = <T>(orgId: string, path: string, init: { method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; body?: unknown } = {}) =>
  request<T>(path, { ...init, organisation: orgId });

/**
 * The brand of one organisation. Five minutes fresh: well inside the half hour the logo's signed
 * path lives, and a brand changes when an administrator changes it — which invalidates this.
 */
export function useBrand(orgId: string | null) {
  return useQuery({
    queryKey: brandKey(orgId ?? 'none'),
    enabled: Boolean(orgId),
    queryFn: () => inOrg<{ brand: Brand }>(orgId ?? '', `/organisations/${orgId}/brand`),
    select: (r) => r.brand,
    staleTime: 5 * 60_000,
  });
}

/** Every brand below an organisation may inherit from it, so a write refreshes them all. */
function invalidateBrands(qc: QueryClient): void {
  void qc.invalidateQueries({
    queryKey: tenancyKeys.orgs,
    predicate: (q) => q.queryKey.at(-1) === 'brand',
  });
}

/** Set the display name; `null` or blank clears it, so the logo stands alone. */
export function useSetBrandName(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string | null) =>
      inOrg<{ brand: Brand }>(orgId, `/organisations/${orgId}/brand`, { method: 'PUT', body: { name } }),
    onSuccess: () => invalidateBrands(qc),
  });
}

const STORAGE_ROOT = `${SUPABASE_URL.replace(/\/+$/, '')}/storage/v1`;

/**
 * The API's three steps: a signed path for one fresh key, the file straight to Storage, then the
 * API confirms what arrived before the organisation names it. The PUT starts at once — the signed
 * path is good for about a minute.
 */
export async function uploadBrandLogo(orgId: string, file: File): Promise<Brand> {
  const started = await inOrg<{ uploadKey: string; uploadPath: string }>(orgId, `/organisations/${orgId}/brand/logo`, {
    method: 'POST',
    body: { contentType: file.type, sizeBytes: file.size },
  });
  let res: Response;
  try {
    res = await fetch(`${STORAGE_ROOT}${started.uploadPath}`, {
      method: 'PUT',
      headers: { 'content-type': file.type },
      body: file,
    });
  } catch {
    throw new StorageUploadError(0);
  }
  if (!res.ok) throw new StorageUploadError(res.status);
  const done = await inOrg<{ brand: Brand }>(orgId, `/organisations/${orgId}/brand/logo/complete`, {
    method: 'POST',
    body: { uploadKey: started.uploadKey },
  });
  return done.brand;
}

export function useUploadBrandLogo(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadBrandLogo(orgId, file),
    onSuccess: () => invalidateBrands(qc),
  });
}

export function useRemoveBrandLogo(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => inOrg<{ brand: Brand }>(orgId, `/organisations/${orgId}/brand/logo`, { method: 'DELETE' }),
    onSuccess: () => invalidateBrands(qc),
  });
}

/** A file's problem before it is sent, in the words the form shows; null when it may go. */
export function logoProblem(file: Pick<File, 'type' | 'size'>): string | null {
  if (!(LOGO_TYPES as readonly string[]).includes(file.type)) return 'A logo must be a PNG, JPEG or WebP image.';
  if (file.size <= 0) return 'That file is empty.';
  if (file.size > MAX_LOGO_BYTES) return `A logo is at most ${MAX_LOGO_BYTES / (1024 * 1024)} MB.`;
  return null;
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.readAsDataURL(blob);
  });

/**
 * The picture for a brand, as something an `<img>` may show: the copy this browser kept for the
 * organisation when it matches `updatedAt`, else the stored logo fetched into a `blob:` URL (and
 * kept, when small). Null while there is no logo or it has not arrived.
 *
 * `keepFor` names the organisation whose console this is; without it nothing is kept — the
 * Organisation screen's preview of another organisation must not overwrite the header's copy.
 */
export function useBrandLogoUrl(view: BrandView | undefined, keepFor?: string): string | null {
  const kept: KeptBrand | null = keepFor ? readKeptBrand(keepFor) : null;
  const matches = kept !== null && view !== undefined && kept.updatedAt === view.updatedAt;
  const [url, setUrl] = useState<string | null>(null);
  const path = view?.logo?.path ?? null;

  useEffect(() => {
    if (path === null || matches) {
      setUrl(null);
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    fetchStoredImage(path, controller.signal)
      .then(async (blob) => {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        if (keepFor && view) {
          keepBrand(keepFor, { updatedAt: view.updatedAt, name: view.name, dataUrl: await blobToDataUrl(blob) });
        }
      })
      .catch(() => {
        /* A logo that will not load leaves the name standing alone, which is a legible header. */
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // `view` is read only for what to keep, which `path` and `matches` already key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, matches, keepFor]);

  if (view !== undefined && view.logo === null) return null;
  if (matches) return kept.dataUrl;
  return url ?? (view === undefined ? (kept?.dataUrl ?? null) : null);
}

/** What this browser kept of the organisation's brand, for the paint before the query answers. */
export function keptBrandFor(orgId: string): KeptBrand | null {
  return readKeptBrand(orgId);
}
